import { createSignal, onCleanup, onMount, For, Show } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import { A } from '@solidjs/router';
import { useApi, isOk } from '../api';
import { useToast } from '../components/MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import Text from '../components/Text';
import BotStatusBadge, { type BotState } from '../components/BotStatusBadge';
import { BOT_ACTIONS, botAccessLabelKey, can } from '../services/resourceActions';
import { useActiveBot } from '../state/activeBot';
import { SearchInput } from '../components/SearchInput';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import PlayerHeadAvatar from '../components/PlayerHeadAvatar';
import BotGroupsSection from '../components/BotGroupsSection';
import styles from './BotManagement.module.css';

interface BotEntry {
    id: string;
    minecraftUsername: string;
    minecraftUuid: string;
    isOwner: boolean;
    myActions: string[];
    state?: BotState;
}

// Tiers mirror botAccessLabelKey so the colour and the word can never disagree.
function accessBadgeColor(actions: readonly string[]): string {
    if (can(actions, BOT_ACTIONS.AccessGrant)) return 'var(--error-500)';
    if (can(actions, BOT_ACTIONS.Control)) return 'var(--primary-400)';
    return 'var(--dark-text-muted)';
}

export default function BotManagement() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const { canViewAll } = useActiveBot();

    const [bots, setBots] = createStore<BotEntry[]>([]);
    const [search, setSearch] = createSignal('');
    const matchesSearch = (bot: BotEntry) => {
        const q = search().trim().toLowerCase();
        if (!q) return true;
        return bot.minecraftUsername.toLowerCase().includes(q)
            || bot.minecraftUuid.toLowerCase().includes(q);
    };
    const byUsername = (a: BotEntry, b: BotEntry) => a.minecraftUsername.localeCompare(b.minecraftUsername);
    const ownBots = () => bots.filter(b => b.isOwner && matchesSearch(b)).sort(byUsername);
    const sharedBots = () => bots.filter(b => !b.isOwner && matchesSearch(b)).sort(byUsername);
    const [loading, setLoading] = createSignal(true);
    const [linkLoading, setLinkLoading] = createSignal(false);
    const [bedrockLinkLoading, setBedrockLinkLoading] = createSignal(false);
    const bedrockEnabled = useFeatureFlag('BEDROCK');

    const fetchBots = async (silent = false) => {
        if (!silent) setLoading(true);
        const res = canViewAll()
            ? await api.botRegistry.listBots({ all: true })
            : await api.botRegistry.listBots();
        if (isOk(res.data)) {
            const incoming = (res.data.value.bots ?? []) as unknown as BotEntry[];
            setBots(reconcile(incoming, { key: 'id' }));
        }
        if (!silent) setLoading(false);
    };

    const BOT_LIST_POLL_MS = 3000;

    onMount(async () => {
        await fetchBots();
        const handle = window.setInterval(() => { void fetchBots(true); }, BOT_LIST_POLL_MS);
        onCleanup(() => clearInterval(handle));
    });

    const startLink = async (
        initiate: () => Promise<{ data: { status?: number; value?: { state: string; authorizationUrl: string } } }>,
        setBusy: (busy: boolean) => void,
        failedKey: string,
    ) => {
        setBusy(true);
        const res = await initiate();
        setBusy(false);
        if (isOk(res.data)) {
            const nonce = Math.random().toString(36).substring(2);
            const cookieData = JSON.stringify({ next: '/bots', backendState: res.data.value.state });
            const expires = new Date();
            expires.setTime(expires.getTime() + 10 * 60 * 1000);
            document.cookie = `${nonce}=${encodeURIComponent(cookieData)};expires=${expires.toUTCString()};path=/`;

            const url = new URL(res.data.value.authorizationUrl);
            url.searchParams.set('state', nonce);
            window.location.href = url.toString();
        } else {
            toast?.error(t(failedKey));
        }
    };

    const linkNewBot = () => startLink(
        () => api.botRegistry.linkNewBot({ origin: window.location.origin }),
        setLinkLoading,
        'BOT_REGISTRY_LINK_FAILED');

    const linkNewBedrockBot = () => startLink(
        () => api.botRegistry.linkNewBedrockBot({ origin: window.location.origin }),
        setBedrockLinkLoading,
        'BOT_REGISTRY_LINK_BEDROCK_FAILED');

    const renderBotCard = (bot: BotEntry, index: () => number) => (
        <div class={`fcard ${styles.botCard}`} style={{ '--i': index() } as any}>
            <PlayerHeadAvatar name={bot.minecraftUuid} size={40} />
            <div class={styles.botInfo}>
                <div class={styles.botName}>{bot.minecraftUsername}</div>
                <div class={styles.botUuid}>{bot.minecraftUuid}</div>
            </div>
            <div class={styles.botMeta}>
                <BotStatusBadge state={bot.state} />
                <span
                    class={styles.accessBadge}
                    style={{ background: bot.isOwner ? 'var(--primary-400)' : accessBadgeColor(bot.myActions) }}
                >
                    {t(botAccessLabelKey(bot.myActions))}
                </span>
                <A href={`/bots/${bot.id}/manage`} class={`btn btn-secondary btn-sm ${styles.settingsButton}`}>
                    <i class="fa-solid fa-gear" /> <Text val="BOT_REGISTRY_SETTINGS" />
                </A>
            </div>
        </div>
    );

    const renderSection = (icon: string, titleKey: string, sectionBots: () => BotEntry[]) => (
        <Show when={sectionBots().length > 0}>
            <h2 class={styles.sectionTitle}>
                <i class={`fa-solid ${icon}`} /> <Text val={titleKey} />
                <span class={styles.sectionCount}>({sectionBots().length})</span>
            </h2>
            <div class={`list-stagger ${styles.list}`}>
                <For each={sectionBots()}>{(bot, index) => renderBotCard(bot, index)}</For>
            </div>
        </Show>
    );

    return (
        <div class="dashboard-page">
            <div class={styles.header}>
                <h1><i class={`fa-solid fa-robot ${styles.titleIcon}`} /><Text val="BOT_REGISTRY_TITLE" /></h1>
                <div class={styles.headerActions}>
                    <button
                        class="btn btn-primary"
                        onClick={linkNewBot}
                        disabled={linkLoading()}
                        attr:data-loading={linkLoading() ? "true" : undefined}
                    >
                        <i class="fa-brands fa-microsoft" /> <Text val="BOT_REGISTRY_LINK_NEW" />
                    </button>
                    <Show when={bedrockEnabled()}>
                        <button
                            class="btn btn-secondary"
                            onClick={linkNewBedrockBot}
                            disabled={bedrockLinkLoading()}
                            attr:data-loading={bedrockLinkLoading() ? "true" : undefined}
                        >
                            <i class="fa-solid fa-cubes" /> <Text val="BOT_REGISTRY_LINK_BEDROCK" />
                        </button>
                    </Show>
                </div>
            </div>

            <Show when={loading()}>
                <div class={styles.loading}><i class="fa-solid fa-spinner fa-spin fa-2x" /></div>
            </Show>

            <Show when={!loading() && bots.length === 0}>
                <div class={`fcard v ${styles.emptyCard}`}>
                    <i class="fa-solid fa-robot" />
                    <p><Text val="BOT_REGISTRY_EMPTY" /></p>
                </div>
            </Show>

            <Show when={!loading() && bots.length > 0}>
                <SearchInput
                    value={search()}
                    onInput={setSearch}
                    placeholder={t('BOT_REGISTRY_SEARCH_PLACEHOLDER')}
                />
            </Show>

            <Show when={!loading() && bots.length > 0 && ownBots().length === 0 && sharedBots().length === 0}>
                <div class={`fcard v ${styles.emptyCard}`}>
                    <i class="fa-solid fa-magnifying-glass" />
                    <p><Text val="BOT_REGISTRY_SEARCH_EMPTY" /></p>
                </div>
            </Show>

            {renderSection('fa-user', 'BOT_REGISTRY_SECTION_OWN', ownBots)}
            {renderSection('fa-user-group', 'BOT_REGISTRY_SECTION_SHARED', sharedBots)}

            <Show when={!loading() && bots.length > 0}>
                <BotGroupsSection bots={bots} />
            </Show>
        </div>
    );
}
